import React, { useEffect, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Card,
  CardBody,
  Heading,
  HStack,
  SimpleGrid,
  Spinner,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useUser } from "../../contexts/UserContext";
import { isOrganizationAdmin } from "../../utils/authRoles";
import OrganizationSurveyConfigModal from "./OrganizationSurveyConfigModal";
import TourModeActionButton from "./TourModeActionButton";
import { formatOrganizationSurveyTitle } from "../../utils/organizationSurveyHelper";
import {
  fetchOrganizationSurveys,
  getOrganizationSurvey,
  updateOrganizationSurvey,
} from "./organizationSurveysApi";

const SurveyMetric = ({ label, value, helpText }) => (
  <Stat p={4} borderWidth="1px" borderRadius="lg">
    <StatLabel>{label}</StatLabel>
    <StatNumber>{value ?? 0}</StatNumber>
    {helpText ? <StatHelpText>{helpText}</StatHelpText> : null}
  </Stat>
);

const isSurveyTitleTranslationKey = (value) =>
  typeof value === "string" && value.includes("reports.layout.");

const toIsoDateString = (value) => {
  if (!value) return null;
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
};

const OrganizationSurveysPage = () => {
  const { t } = useTranslation();
  const { user } = useUser();
  const toast = useToast();
  const configModal = useDisclosure();
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const [isSavingConfig, setIsSavingConfig] = useState(false);
  const organizationId = user?.organization?.id ?? user?.organization_id;
  const isTour = user?.organization?.tour_mode === true;
  const canConfigureSurveys = isOrganizationAdmin(user);
  const getSurveyTitle = (name) => {
    if (isSurveyTitleTranslationKey(name)) {
      return t(name, name);
    }

    return name;
  };

  const getFormattedSurveyTitle = (survey) =>
    formatOrganizationSurveyTitle(survey, {
      translateSurveyName: getSurveyTitle,
    });

  const loadSurveys = async ({ active } = {}) => {
    try {
      setLoading(true);
      setError("");
      const nextSurveys = await fetchOrganizationSurveys();
      if (active === false) return;
      setSurveys(nextSurveys);
    } catch (err) {
      if (active === false) return;
      setError(t("organization.surveys_error", "Could not load your survey cycles."));
    } finally {
      if (active !== false) setLoading(false);
    }
  };

  useEffect(() => {
    let active = true;

    loadSurveys({ active });

    return () => {
      active = false;
    };
  }, [t]);

  const openConfigModal = async (survey) => {
    if (!organizationId) return;

    setSelectedSurvey({
      organization_survey_id: survey.organization_survey_id,
      name: survey.name,
      survey_period: survey.survey_period,
      roles: Array.isArray(survey.roles) ? survey.roles : [],
    });
    setIsLoadingConfig(true);
    configModal.onOpen();

    try {
      const fullSurvey = await getOrganizationSurvey(
        organizationId,
        survey.organization_survey_id
      );
      setSelectedSurvey((prev) => ({
        ...fullSurvey,
        organization_survey_id:
          fullSurvey.organization_survey_id ||
          fullSurvey.id ||
          prev?.organization_survey_id,
        name: fullSurvey.name || prev?.name,
        survey_period: fullSurvey.survey_period || prev?.survey_period,
      }));
    } catch (err) {
      toast({
        title: t("organization.surveys_config_error_title", "Could not save survey"),
        description: t(
          "organization.surveys_config_load_error_description",
          "Could not load the survey configuration."
        ),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setSelectedSurvey(null);
      configModal.onClose();
    } finally {
      setIsLoadingConfig(false);
    }
  };

  const closeConfigModal = () => {
    if (isSavingConfig || isLoadingConfig) return;
    setSelectedSurvey(null);
    configModal.onClose();
  };

  const handleConfigSave = async (payload) => {
    if (!organizationId) {
      console.error("Missing organizationId");
      return;
    }

    const surveyId =
      selectedSurvey?.organization_survey_id ||
      selectedSurvey?.id;

    if (!surveyId) {
      console.error("Missing survey id", selectedSurvey);
      return;
    }

    const normalizedPayload = {
      start_date: toIsoDateString(payload?.start_date),
      end_date: toIsoDateString(payload?.end_date),
      roles: Array.isArray(payload?.roles) ? payload.roles : [],
      opening_message: payload?.opening_message ?? "",
      closing_message: payload?.closing_message ?? "",
      passcode: payload?.passcode ?? "",
    };

    console.log("Submitting survey config:", normalizedPayload);
    console.log("Using surveyId:", surveyId);

    setIsSavingConfig(true);
    try {
      await updateOrganizationSurvey(
        organizationId,
        surveyId,
        normalizedPayload
      );
      await loadSurveys();
      toast({
        title: t("organization.surveys_config_saved_title", "Survey updated"),
        description: t(
          "organization.surveys_config_saved_description",
          "Survey configuration has been saved."
        ),
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      setSelectedSurvey(null);
      configModal.onClose();
    } catch (err) {
      toast({
        title: t("organization.surveys_config_error_title", "Could not save survey"),
        description: t(
          "organization.surveys_config_error_description",
          "Please review the survey settings and try again."
        ),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSavingConfig(false);
    }
  };

  if (loading) {
    return (
      <Box py={10} textAlign="center">
        <Spinner size="xl" />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        {error}
      </Alert>
    );
  }

  return (
    <VStack align="stretch" spacing={6}>
      <Box>
        <Heading size="lg">{t("organization.surveys", "Surveys")}</Heading>
        <Text color="gray.600" mt={2}>
          {t(
            "organization.surveys_description",
            "Manage your school's surveys"
          )}
        </Text>
      </Box>

      {surveys.length === 0 ? (
        <Card>
          <CardBody>
            <Text color="gray.600">
              {t("organization.surveys_empty", "No surveys available")}
            </Text>
          </CardBody>
        </Card>
      ) : (
        <VStack align="stretch" spacing={4}>
          {surveys.map((survey) => (
            <Card key={survey.organization_survey_id}>
              <CardBody>
                <VStack align="stretch" spacing={4}>
                  <Box>
                    <Heading size="md">{getFormattedSurveyTitle(survey)}</Heading>
                    <Text color="gray.600" mt={2}>
                      {t(
                        "organization.surveys_cycle_copy",
                        "Use this cycle as the starting point for report review and workbook follow-up."
                      )}
                    </Text>
                  </Box>

                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                    <SurveyMetric
                      label={t("organization.surveys_respondents", "Respondents")}
                      value={survey.total_respondents}
                      helpText={
                        survey.completion_rate != null
                          ? `${survey.completion_rate}% ${t(
                              "organization.surveys_completion_rate",
                              "completion"
                            )}`
                          : undefined
                      }
                    />
                    <SurveyMetric
                      label={t("organization.surveys_completed", "Completed surveys")}
                      value={survey.completed_surveys}
                      helpText={
                        survey.pages_count != null
                          ? `${survey.pages_count} ${t("organization.surveys_pages", "pages")}`
                          : undefined
                      }
                    />
                    <SurveyMetric
                      label={t("organization.surveys_questions", "Questions")}
                      value={survey.questions_count}
                      helpText={
                        survey.language
                          ? `${t("organization.surveys_language", "Language")}: ${String(
                              survey.language
                            ).toUpperCase()}`
                          : undefined
                      }
                    />
                  </SimpleGrid>

                  <HStack
                    spacing={3}
                    flexWrap="wrap"
                    align="center"
                    data-testid={`survey-actions-${survey.organization_survey_id}`}
                  >
                    {canConfigureSurveys ? (
                      <Box>
                        <TourModeActionButton
                          onClick={() => openConfigModal(survey)}
                          colorScheme="gray"
                          variant="outline"
                          isTour={isTour}
                        >
                          {t("organization.configure", "Configure")}
                        </TourModeActionButton>
                      </Box>
                    ) : null}
                    <Box>
                      <Button
                        as={RouterLink}
                        to={`/app/organization/results/${survey.organization_survey_id}`}
                        state={{
                          surveyTitle: getFormattedSurveyTitle(survey),
                          survey,
                        }}
                        colorScheme="blue"
                        variant="outline"
                      >
                        {t("organization.view_results", "View Results")}
                      </Button>
                    </Box>
                    <Box>
                      {survey.workbook_usable === false ? (
                        <VStack align="start" spacing={1}>
                          <Button isDisabled colorScheme="blue">
                            {t("organization.open_workbook", "Open Workbook")}
                          </Button>
                          <Text fontSize="sm" color="gray.600">
                            {t(
                              "organization.workbook_unavailable_for_survey",
                              "Workbook not available yet for this survey"
                            )}
                          </Text>
                        </VStack>
                      ) : (
                        <Button
                          as={RouterLink}
                          to="/app/organization/workbook"
                          state={{
                            organizationSurveyId: survey.organization_survey_id,
                          }}
                          colorScheme="blue"
                        >
                          {t("organization.open_workbook", "Open Workbook")}
                        </Button>
                      )}
                    </Box>
                  </HStack>
                </VStack>
              </CardBody>
            </Card>
          ))}
        </VStack>
      )}

      <OrganizationSurveyConfigModal
        isOpen={configModal.isOpen}
        onClose={closeConfigModal}
        onSave={handleConfigSave}
        survey={selectedSurvey}
        surveyTitle={getFormattedSurveyTitle(selectedSurvey)}
        isLoadingSurvey={isLoadingConfig}
        isSubmitting={isSavingConfig}
        isTour={isTour}
      />
    </VStack>
  );
};

export default OrganizationSurveysPage;
